import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import appLogo from "assets/logo/logo-only.png";
import {
  Image,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  PDFDownloadLink,
} from "@react-pdf/renderer";
import CustomModal from "components/Modal";
import Button from "components/Button";
import { format } from "date-fns";
import "./index.scss";
import { getProducts } from "api/Products";
import Receipt from "./components/Receipt";
import Invoice from "./components/Invoice";

const OrderReceipt = ({
  isOpen,
  onClose,
  order,
  token,
  payment,
  startDate,
  endDate,
  farms,
}) => {
  const [product, setProduct] = useState([]);
  // order data is passed. format dates and amounts.
  const formatIssuedDate = order?.created_on
    ? format(new Date(order?.created_on), "dd MMM yyyy")
    : "N/A";
  const formatPaymentDate = payment?.updated_on
    ? format(new Date(order?.created_on), "dd MMM yyyy")
    : "N/A";
  const formatSubTotal = order?.order_subtotal / 100;
  const formatTotal = order?.order_total / 100;
  const formatGST = order?.gst / 100;
  const invoiceType =
    order?.status === "cancelled" || order?.status === "pending"
      ? "Tax Invoice"
      : "Tax Receipt";

  useEffect(() => {
    // get product details from orders.
    const fetchProduct = async (productId) => {
      const prodDetails = await getProducts(token);
      const selectedProduct = prodDetails.data.filter(
        (prod) => prod.id === productId
      );
      setProduct(selectedProduct);
    };

    if (order.product_id) {
      fetchProduct(order.product_id);
    }
  }, [order, token]);

  const styles = StyleSheet.create({
    page: { paddingTop: 35, paddingBottom: 65, paddingHorizontal: 35 },
    logo: {
      width: "28px",
      height: "32px",
      position: "absolute",
      top: 0,
      left: 0,
    },
    type: {
      marginLeft: 28,
      fontSize: 14,
      marginBottom: 16,
      marginTop: 2,
      padding: 6,
      justifyContent: "center",
    },
    title: {
      fontSize: 10,
      fontStyle: "bold",
      border: 1,
      borderColor: "gray",
      borderStyle: "solid",
      borderRadius: "5px",
      width: "60%",
      justifyContent: "center",
      textAlign: "center",
      alignContent: "center",
      padding: 2,
    },
    label: { color: "gray", fontSize: 10 },
    labelTop: {
      color: "gray",
      fontSize: 10,
      marginTop: 2,
      marginBottom: 2,
      textAlign: "left",
    },
    labelBot: {
      color: "gray",
      fontSize: 10,
      marginBottom: 2,
      marginTop: 2,
      textAlign: "left",
    },
    address: {
      fontSize: 10,
      textAlign: "left",
      marginBottom: 0.5,
      color: "gray",
    },
    text: {
      fontSize: 12,
      color: "black",
      marginBottom: 1,
      marginTop: 1,
      textAlign: "left",
    },
    section1: {
      flex: 0.3,
      textAlign: "center",
      backgroundColor: "white",
      flexWrap: "wrap",
      flexDirection: "row",
    },
    group: { flexBasis: "30%" },
    linediv: { borderBottom: 1, borderBottomColor: "gray" },
    section2: {
      flex: 0.3,
      textAlign: "center",
      backgroundColor: "white",
      flexWrap: "wrap",
      flexDirection: "row",
      marginTop: 16,
      marginBottom: 0,
    },
    group2: { flexBasis: "30%" },
    labelTop2: { color: "gray", fontSize: 12, marginTop: 8, textAlign: "left" },
    labelBot2: {
      color: "gray",
      fontSize: 10,
      marginBottom: 2,
      textAlign: "left",
    },
    text2: {
      fontSize: 12,
      color: "black",
      marginBottom: 1,
      marginTop: 1,
      textAlign: "left",
    },
    sec2title: { fontSize: 14, textAlign: "left" },
    section3: {
      flex: 0.3,
      textAlign: "center",
      backgroundColor: "white",
      flexWrap: "wrap",
      flexDirection: "row",
      marginTop: 2,
      border: 2,
      borderRadius: "5",
      borderColor: "black",
      padding: 12,
    },
    group3: { flexBasis: "30%" },
    section4: {
      flex: 0.5,
      textAlign: "center",
      flexWrap: "wrap",
      flexDirection: "row",
      marginTop: 4,
      padding: 16,
    },
    group4: { flexBasis: "25%", padding: 2 },
    receipt: {
      flex: 0.5,
      textAlign: "center",
      flexWrap: "wrap",
      flexDirection: "row",
    },
    group5: { flexBasis: "25%", padding: 2 },
  });

  const InvoicePdf = () => (
    <Document>
      <Page size="Legal" style={styles.page}>
        <View>
          <Image src={appLogo} style={styles.logo} />
          <Text style={styles.type}>{invoiceType}</Text>
        </View>
        {/* Invoice Details */}
        <View style={[styles.section1, { color: "black" }]}>
          <View style={styles.group}>
            <Text style={styles.title}>Invoice Details</Text>
          </View>
          <View style={styles.group}>
            <Text style={styles.text}>
              {formatTotal.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
            <Text style={styles.labelTop}>Amount Due</Text>
          </View>
          <View style={styles.group}>
            <Text />
          </View>
          <View style={styles.group}>
            <Text />
          </View>
          <View style={styles.group}>
            <Text style={styles.labelBot}>Invoice Number</Text>
            <Text style={styles.text}>{payment?.invoice_number}</Text>
          </View>
          <View style={styles.group}>
            <Text style={styles.text}>{formatIssuedDate}</Text>
            <Text style={styles.labelTop}>Date Issued</Text>
          </View>
          <View style={styles.group}>
            <Text />
          </View>
          <View style={styles.group}>
            <Text style={styles.text}>{order?.id}</Text>
            <Text style={styles.labelBot}>Reference Number</Text>
          </View>
          <View style={styles.group}>
            <Text style={styles.text}>56 619 577 150</Text>
            <Text style={styles.labelBot}>ABN</Text>
          </View>
        </View>
        <View style={styles.linediv} />
        {/* Billed From */}
        <View style={[styles.section2, { color: "black" }]}>
          <View style={styles.group2}>
            <Text style={styles.title}>Billed From</Text>
          </View>
          <View style={styles.group2}>
            <Text style={styles.text2}>Data Farming Pty Ltd.</Text>
            <Text style={styles.address}>
              P.O.Box 253, Highfields, Toowoomba
            </Text>
            <Text style={styles.address}>Queensland 4352</Text>
            <Text style={styles.address}>Australia</Text>
            <Text style={styles.address}>accounts@datafarming.com.au</Text>
          </View>
          <View style={styles.group2}>
            <Text />
          </View>
        </View>
        <View style={styles.linediv} />
        {/* Billed To */}
        <View style={[styles.section2, { color: "black" }]}>
          <View style={styles.group2}>
            <Text style={styles.title}>Billed To</Text>
          </View>
          <View style={styles.group2}>
            <Text style={styles.text2}>
              {order?.first_name} {order?.last_name}
            </Text>
            <Text style={styles.address}>
              {order?.street} {order?.state}
            </Text>
            <Text style={styles.address}>
              {order?.suburb} {order?.post_code}
            </Text>
            <Text style={styles.address}>{order?.country}</Text>
            <Text style={styles.address} />
          </View>
          <View style={styles.group2}>
            <Text />
          </View>
        </View>
        {/* Payment Terms */}
        <View style={[styles.section3, { color: "black" }]}>
          <View style={styles.group3}>
            <Text style={styles.sec2title}>Payment Terms</Text>
            <Text style={styles.labelBot2}>To be paid within 14 days</Text>
          </View>
          <View style={styles.group3}>
            <Text />
          </View>
          <View style={styles.group3}>
            <Text />
          </View>
          <View style={styles.group3}>
            <Text style={[styles.title, { marginTop: "4px" }]}>
              Deposit Details
            </Text>
          </View>
          <View style={styles.group3}>
            <Text style={styles.text}>
              {formatTotal.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
            <Text style={styles.labelTop}>Amount Due</Text>
          </View>
          <View style={styles.group3}>
            <Text />
          </View>
          <View style={styles.group3}>
            <Text />
          </View>
          <View style={styles.group3}>
            <Text style={styles.text}>034-008</Text>
            <Text style={styles.labelTop}>BSB</Text>
          </View>
          <View style={styles.group3}>
            <Text style={styles.text}>376630</Text>
            <Text style={styles.labelTop}>Account Number</Text>
          </View>
          <View style={styles.group3}>
            <Text />
          </View>
          <View style={styles.group3}>
            <Text style={styles.text}>Data Farming Pty Ltd</Text>
            <Text style={styles.labelBot}>Name of Account</Text>
          </View>
          <View style={styles.group3}>
            <Text style={styles.text}>WPACAU2S</Text>
            <Text style={styles.labelBot}>Swift Code</Text>
          </View>
        </View>
        {/* Order Summary */}
        <View style={[styles.section4, { color: "black" }]}>
          <View style={styles.group4}>
            <Text style={styles.sec2title}>Order Summary</Text>
          </View>
          <View style={styles.group4}>
            <Text />
          </View>
          <View style={styles.group4}>
            <Text />
          </View>
          <View style={styles.group4}>
            <Text />
          </View>
          <View style={styles.group4}>
            <Text style={[styles.label, { marginRight: "220px" }]}>Item</Text>
          </View>
          <View style={styles.group4}>
            <Text style={[styles.label, { textAlign: "right" }]}>
              Unit Price Per Area
            </Text>
          </View>
          <View style={styles.group4}>
            <Text style={[styles.label, { textAlign: "right" }]}>
              Total Area
            </Text>
          </View>
          <View style={styles.group4}>
            <Text style={[styles.label, { textAlign: "right" }]}>
              Total Cost
            </Text>
          </View>
          <View style={styles.group4}>
            {product && (
              <Text
                style={[styles.sec2title, { width: "100%", textAlign: "left" }]}
              >
                {product[0]?.title}
              </Text>
            )}
            <Text
              style={[
                styles.label,
                { width: "100%", textAlign: "left", marginTop: "8px" },
              ]}
            >
              FARMS
            </Text>
            {farms
              ? farms.map((farm) => (
                  <Text
                    style={[
                      styles.label,
                      { width: "100%", textAlign: "left", marginTop: "1px" },
                    ]}
                  >
                    {farm?.newFarms?.properties?.farm_name}
                  </Text>
                ))
              : null}

            <Text
              style={[
                styles.label,
                { width: "100%", textAlign: "left", marginTop: "8px" },
              ]}
            >
              DETAILS
            </Text>
            <Text
              style={[
                styles.label,
                { width: "100%", textAlign: "left", marginTop: "1px" },
              ]}
            >
              {order?.specification?.config?.number_of_captures} Captures /{" "}
              {order?.specification?.field_ids?.length} Fields
            </Text>
            <Text style={[styles.label, { width: "100%", textAlign: "left" }]}>
              Capture Range from {startDate} to {endDate}
            </Text>
            <Text style={[styles.label, { width: "100%", textAlign: "left" }]}>
              {order?.minimum_hectare_requirements_pricing
                ? "** Minimum  pricing affects this order"
                : ""}
            </Text>
          </View>
          <View style={styles.group4}>
            <Text style={[styles.text, { textAlign: "right" }]}>
              {formatTotal.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
          </View>
          <View style={styles.group4}>
            <Text style={[styles.text, { textAlign: "right" }]}>
              {order?.total_product_unit?.toFixed(2)}
            </Text>
          </View>
          <View style={styles.group4}>
            <Text style={[styles.text, { textAlign: "right" }]}>
              {formatTotal.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
          </View>
          <View style={styles.group4}>
            <Text
              style={[
                styles.label,
                { textAlign: "left", marginTop: "12px", marginBottom: "1px" },
              ]}
            >
              Subtotal
            </Text>
          </View>
          <View style={styles.group4}>
            <Text />
          </View>
          <View style={styles.group4}>
            <Text />
          </View>
          <View style={styles.group4}>
            <Text
              style={[
                styles.text,
                { textAlign: "right", marginTop: "12px", marginBottom: "1px" },
              ]}
            >
              {formatSubTotal.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
          </View>
          <View style={styles.group4}>
            <Text
              style={[styles.label, { textAlign: "left", marginBottom: "1px" }]}
            >
              GST
            </Text>
          </View>
          <View style={styles.group4}>
            <Text />
          </View>
          <View style={styles.group4}>
            <Text />
          </View>
          <View style={styles.group4}>
            <Text
              style={[styles.text, { textAlign: "right", marginBottom: "1px" }]}
            >
              {formatGST.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
          </View>
          <View style={styles.group4}>
            <Text
              style={[styles.label, { textAlign: "left", marginBottom: "1px" }]}
            >
              Total Cost
            </Text>
          </View>
          <View style={styles.group4}>
            <Text style={[styles.label, { textAlign: "right" }]} />
          </View>
          <View style={styles.group4}>
            <Text />
          </View>
          <View style={styles.group4}>
            <Text
              style={[styles.text, { textAlign: "right", marginBottom: "1px" }]}
            >
              {formatTotal.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
          </View>
          <View style={styles.group4}>
            <Text
              style={[styles.label, { textAlign: "left", marginBottom: "1px" }]}
            >
              Total Net Payment
            </Text>
          </View>
          <View style={styles.group4}>
            <Text />
          </View>
          <View style={styles.group4}>
            <Text />
          </View>
          <View style={styles.group4}>
            <Text
              style={[styles.text, { textAlign: "right", marginBottom: "1px" }]}
            >
              $0.00
            </Text>
          </View>
          <View style={styles.group4}>
            <Text
              style={[styles.label, { textAlign: "left", marginBottom: "1px" }]}
            >
              Amount Due
            </Text>
          </View>
          <View style={styles.group4}>
            <Text />
          </View>
          <View style={styles.group4}>
            <Text />
          </View>
          <View style={styles.group4}>
            <Text
              style={[styles.text, { textAlign: "right", marginBottom: "1px" }]}
            >
              {formatTotal.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );

  const ReceiptPdf = () => (
    <Document>
      <Page size="A4" style={styles.page}>
        <View>
          <Image src={appLogo} style={styles.logo} />
          <Text style={styles.type}>{invoiceType}</Text>
        </View>
        {/* Receipt Details */}
        <View style={[styles.section1, { color: "black" }]}>
          <View style={styles.group}>
            <Text style={styles.title}>Invoice Details</Text>
          </View>
          <View style={styles.group}>
            <Text style={styles.text}>
              {formatTotal.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
            <Text style={styles.labelTop}>Amount Paid</Text>
          </View>
          <View style={styles.group}>
            <Text />
          </View>
          <View style={styles.group}>
            <Text />
          </View>
          <View style={styles.group}>
            <Text style={styles.labelBot}>Invoice Number</Text>
            <Text style={styles.text}>{payment?.invoice_number}</Text>
          </View>
          <View style={styles.group}>
            <Text style={styles.text}>{formatIssuedDate}</Text>
            <Text style={styles.labelTop}>Date Issued</Text>
          </View>
          <View style={styles.group}>
            <Text />
          </View>
          <View style={styles.group}>
            <Text style={styles.text}>{order?.id}</Text>
            <Text style={styles.labelBot}>Order Number</Text>
          </View>
          <View style={styles.group}>
            <Text style={styles.text}>56 619 577 150</Text>
            <Text style={styles.labelBot}>ABN</Text>
          </View>
        </View>
        <View style={styles.linediv} />
        {/* Billed From */}
        <View style={[styles.section2, { color: "black" }]}>
          <View style={styles.group2}>
            <Text style={styles.title}>Billed From</Text>
          </View>
          <View style={styles.group2}>
            <Text style={styles.text2}>Data Farming Pty Ltd.</Text>
            <Text style={styles.address}>
              P.O.Box 253, Highfields, Toowoomba
            </Text>
            <Text style={styles.address}>Queensland 4352</Text>
            <Text style={styles.address}>Australia</Text>
            <Text style={styles.address}>accounts@datafarming.com.au</Text>
          </View>
          <View style={styles.group2}>
            <Text />
          </View>
        </View>
        <View style={styles.linediv} />
        {/* Billed To */}
        <View style={[styles.section2, { color: "black" }]}>
          <View style={styles.group2}>
            <Text style={styles.title}>Billed To</Text>
          </View>
          <View style={styles.group2}>
            <Text style={styles.text2}>
              {order?.first_name} {order?.last_name}
            </Text>
            <Text style={styles.address}>
              {order?.street} {order?.state}
            </Text>
            <Text style={styles.address}>
              {order?.suburb} {order?.post_code}
            </Text>
            <Text style={styles.address}>{order?.country}</Text>
            <Text style={styles.address} />
          </View>
          <View style={styles.group2}>
            <Text />
          </View>
        </View>
        {/* Order Summary */}
        <View style={[styles.receipt, { color: "black" }]}>
          <View style={styles.group4}>
            <Text style={styles.sec2title}>Order Summary</Text>
          </View>
          <View style={styles.group5}>
            <Text />
          </View>
          <View style={styles.group5}>
            <Text />
          </View>
          <View style={styles.group5}>
            <Text />
          </View>
          <View style={styles.group5}>
            <Text style={[styles.label, { marginRight: "220px" }]}>Item</Text>
          </View>
          <View style={styles.group5}>
            <Text style={[styles.label, { textAlign: "right" }]}>
              Unit Price Per Area
            </Text>
          </View>
          <View style={styles.group5}>
            <Text style={[styles.label, { textAlign: "right" }]}>
              Total Area
            </Text>
          </View>
          <View style={styles.group5}>
            <Text style={[styles.label, { textAlign: "right" }]}>
              Total Cost
            </Text>
          </View>
          <View style={styles.group5}>
            {product && (
              <Text
                style={[styles.sec2title, { width: "100%", textAlign: "left" }]}
              >
                {product[0]?.title}
              </Text>
            )}
            <Text
              style={[
                styles.label,
                { width: "100%", textAlign: "left", marginTop: "8px" },
              ]}
            >
              FARMS
            </Text>
            {farms
              ? farms.map((farm) => (
                  <Text
                    style={[
                      styles.label,
                      { width: "100%", textAlign: "left", marginTop: "1px" },
                    ]}
                  >
                    {farm?.newFarms?.properties?.farm_name}
                  </Text>
                ))
              : null}

            <Text
              style={[
                styles.label,
                { width: "100%", textAlign: "left", marginTop: "8px" },
              ]}
            >
              DETAILS
            </Text>
            <Text
              style={[
                styles.label,
                { width: "100%", textAlign: "left", marginTop: "1px" },
              ]}
            >
              {order?.specification?.config?.number_of_captures} Captures /{" "}
              {order?.specification?.field_ids?.length} Fields
            </Text>
            <Text style={[styles.label, { width: "100%", textAlign: "left" }]}>
              Capture Range from {startDate} to {endDate}
            </Text>
            <Text style={[styles.label, { width: "100%", textAlign: "left" }]}>
              {order?.minimum_hectare_requirements_pricing
                ? "** Minimum  pricing affects this order"
                : ""}
            </Text>
          </View>
          <View style={styles.group5}>
            <Text style={[styles.text, { textAlign: "right" }]}>
              {formatTotal.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
          </View>
          <View style={styles.group5}>
            <Text style={[styles.text, { textAlign: "right" }]}>
              {order?.total_product_unit?.toFixed(2)}
            </Text>
          </View>
          <View style={styles.group5}>
            <Text style={[styles.text, { textAlign: "right" }]}>
              {formatTotal.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
          </View>
          <View style={styles.group5}>
            <Text
              style={[
                styles.label,
                { textAlign: "left", marginTop: "12px", marginBottom: " 4px" },
              ]}
            >
              Subtotal
            </Text>
          </View>
          <View style={styles.group5}>
            <Text />
          </View>
          <View style={styles.group5}>
            <Text />
          </View>
          <View style={styles.group5}>
            <Text
              style={[
                styles.text,
                { textAlign: "right", marginTop: "12px", marginBottom: " 4px" },
              ]}
            >
              {formatSubTotal.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
          </View>
          <View style={styles.group5}>
            <Text
              style={[
                styles.label,
                { textAlign: "left", marginBottom: " 4px" },
              ]}
            >
              GST
            </Text>
          </View>
          <View style={styles.group5}>
            <Text />
          </View>
          <View style={styles.group5}>
            <Text />
          </View>
          <View style={styles.group5}>
            <Text
              style={[
                styles.text,
                { textAlign: "right", marginBottom: " 4px" },
              ]}
            >
              {formatGST.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
          </View>
          <View style={styles.group5}>
            <Text
              style={[
                styles.label,
                { textAlign: "left", marginBottom: " 4px" },
              ]}
            >
              Total Cost
            </Text>
          </View>
          <View style={styles.group5}>
            <Text style={[styles.label, { textAlign: "right" }]} />
          </View>
          <View style={styles.group5}>
            <Text />
          </View>
          <View style={styles.group5}>
            <Text
              style={[
                styles.text,
                { textAlign: "right", marginBottom: " 4px" },
              ]}
            >
              {formatTotal.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );

  return (
    <CustomModal
      isOpen={isOpen}
      onRequestClose={onClose}
      onClose={onClose}
      label="Receipt"
      size="invoice"
    >
      <div className="order-receipt" data-testid="order-receipt">
        {(order && order.status === "done") ||
        order.status === "purchased" ||
        order.status === "submitted" ||
        order.status === "processing" ? (
          <Receipt
            order={order}
            payment={payment}
            formatGST={formatGST}
            formatTotal={formatTotal}
            formatIssuedDate={formatIssuedDate}
            formatPaymentDate={formatPaymentDate}
            product={product}
            formatSubTotal={formatSubTotal}
            title={invoiceType}
            startDate={startDate}
            endDate={endDate}
            farms={farms}
          />
        ) : (
          <Invoice
            order={order}
            payment={payment}
            formatGST={formatGST}
            formatTotal={formatTotal}
            formatIssuedDate={formatIssuedDate}
            formatPaymentDate={formatPaymentDate}
            product={product}
            formatSubTotal={formatSubTotal}
            title={invoiceType}
            startDate={startDate}
            endDate={endDate}
            farms={farms}
          />
        )}
        <div className="button-group">
          <PDFDownloadLink
            document={
              invoiceType === "Tax Receipt" ? ReceiptPdf() : InvoicePdf()
            }
            fileName="receipt.pdf"
            style={{ width: "100%" }}
          >
            {({ loading }) =>
              loading ? (
                "Loading receipt..."
              ) : (
                <Button
                  type="solid"
                  color="blue"
                  label={
                    invoiceType === "Tax Receipt"
                      ? "Download Receipt"
                      : "Download Invoice"
                  }
                  block
                />
              )
            }
          </PDFDownloadLink>
          <Button
            type="outlined"
            color="white"
            label="Close"
            block
            onClick={onClose}
          />
        </div>
      </div>
    </CustomModal>
  );
};

OrderReceipt.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  order: PropTypes.shape({
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    street: PropTypes.string.isRequired,
    suburb: PropTypes.string.isRequired,
    country: PropTypes.string.isRequired,
    post_code: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    created_on: PropTypes.string.isRequired,
    order_subtotal: PropTypes.string.isRequired,
    order_total: PropTypes.string.isRequired,
    gst: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    product_id: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    specification: PropTypes.shape({
      field_ids: PropTypes.string.isRequired,
      config: PropTypes.string.isRequired,
    }).isRequired,
    minimum_hectare_requirements_pricing: PropTypes.string.isRequired,
    total_product_unit: PropTypes.string.isRequired,
  }).isRequired,
  token: PropTypes.string.isRequired,
  payment: PropTypes.shape({
    updated_on: PropTypes.string.isRequired,
    invoice_number: PropTypes.string.isRequired,
  }).isRequired,
  product: PropTypes.shape({
    title: PropTypes.string.isRequired,
  }).isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  farms: PropTypes.shape({
    map: PropTypes.func.isRequired,
  }).isRequired,
};

export default OrderReceipt;
